import type { SurveyPage, SurveyQuestion } from '../../fetchSurvey/types';
import type { SurveyAnswers } from '../../submitSurvey/types';
import type { CustomFieldValueMap } from '../../surveyLogic/types/customFieldValues';
import type { SurveyPaginationRefs } from '../../surveyPagination/types';
import { buildLogicContext } from '../../surveyLogic/context/buildLogicContext';
import { resolveSkipTarget } from '../../surveyLogic/evaluator/evaluateSkipLogics';
import { resolveVisibleSkipTargetPageIndex } from '../../surveyLogic/navigation/findVisiblePageIndex';
import { getNextVisiblePageIndex } from '../../surveyLogic/navigation/getNextVisiblePageIndex';
import { shouldIncludePageInPath } from '../../surveyLogic/visibility';
import { getAnswerableQuestionIdsOnPage } from './earnedProgress';

export function simulateActivePath(
  pages: SurveyPage[],
  values: SurveyAnswers,
  allQuestions: SurveyQuestion[],
  customFieldValues: CustomFieldValueMap = {}
): number[] {
  if (pages.length === 0) return [];

  const logicContext = buildLogicContext(values, allQuestions, customFieldValues);
  const pathPages: number[] = [];
  let pageIndex = 0;

  while (pageIndex < pages.length) {
    const page = pages[pageIndex];
    const questions = page?.questions ?? [];

    if (!shouldIncludePageInPath(questions, logicContext)) {
      const nextVisible = getNextVisiblePageIndex(pages, pageIndex, logicContext);
      if (nextVisible >= pages.length) {
        break;
      }
      pageIndex = nextVisible;
      continue;
    }

    pathPages.push(pageIndex);

    const lastQuestion = questions[questions.length - 1];

    if (lastQuestion?.skipLogics?.length) {
      const skipTarget = resolveSkipTarget(lastQuestion, logicContext, pages, pageIndex, {
        silent: true,
      });

      if (skipTarget?.type === 'END_OF_SURVEY') {
        break;
      }

      if (skipTarget?.type === 'QUESTION') {
        if (skipTarget.pageIndex <= pageIndex) {
          break;
        }

        const visibleSkipPageIndex = resolveVisibleSkipTargetPageIndex(
          pages,
          skipTarget.pageIndex,
          logicContext
        );

        if (visibleSkipPageIndex >= pages.length) {
          break;
        }

        pageIndex = visibleSkipPageIndex;
        continue;
      }
    }

    const nextIndex = getNextVisiblePageIndex(pages, pageIndex, logicContext);
    if (nextIndex >= pages.length) {
      break;
    }
    pageIndex = nextIndex;
  }

  return pathPages;
}

export function getActivePathQuestionIds(
  pages: SurveyPage[],
  pathPageIndices: readonly number[],
  values: SurveyAnswers,
  allQuestions: SurveyQuestion[],
  customFieldValues: CustomFieldValueMap = {}
): Set<string> {
  const logicContext = buildLogicContext(values, allQuestions, customFieldValues);
  const ids = new Set<string>();
  pathPageIndices.forEach(pageIndex => {
    getAnswerableQuestionIdsOnPage(pages[pageIndex]!, logicContext).forEach(id => ids.add(id));
  });
  return ids;
}

export function countAnswerableOnPath(
  pages: SurveyPage[],
  pathPageIndices: readonly number[],
  values: SurveyAnswers,
  allQuestions: SurveyQuestion[],
  customFieldValues: CustomFieldValueMap = {}
): number {
  return getActivePathQuestionIds(
    pages,
    pathPageIndices,
    values,
    allQuestions,
    customFieldValues
  ).size;
}

export function countEarnedOnPath(
  earnedQuestionIds: ReadonlySet<string>,
  pathQuestionIds: ReadonlySet<string>
): number {
  let count = 0;
  earnedQuestionIds.forEach(id => {
    if (pathQuestionIds.has(id)) {
      count += 1;
    }
  });
  return count;
}

export function pathsEqual(a: readonly number[], b: readonly number[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((pageIndex, index) => pageIndex === b[index]);
}

export function pruneEarnedToActivePath(
  refs: SurveyPaginationRefs,
  pages: SurveyPage[],
  values: SurveyAnswers,
  allQuestions: SurveyQuestion[],
  customFieldValues: CustomFieldValueMap = {}
): boolean {
  const pathPages = simulateActivePath(pages, values, allQuestions, customFieldValues);
  const pathQuestionIds = getActivePathQuestionIds(
    pages,
    pathPages,
    values,
    allQuestions,
    customFieldValues
  );
  let changed = false;

  refs.earnedQuestionIds.forEach(id => {
    if (!pathQuestionIds.has(id)) {
      refs.earnedQuestionIds.delete(id);
      changed = true;
    }
  });

  return changed;
}
